import { CategoryConfig, CategoryKey } from '../types';

/** Current listing shape version. Raise it whenever the Listing shape changes. */
export const LISTING_SCHEMA_VERSION = 1;

/**
 * Directory categories. Nav, routes, filters, map pins and admin forms all read
 * from this list. Labels come from i18n: `directory.category.<key>` and
 * `directory.sub.<subcategory>`.
 */
export const CATEGORIES: CategoryConfig[] = [
  {
    key: 'shul',
    slug: 'shuls',
    icon: 'Landmark',
    pinColor: '#1d4ed8',
    subcategories: [],
    defaultHoursMode: 'scheduled',
    usesHours: false,
  },
  {
    key: 'gemach',
    slug: 'gemachim',
    icon: 'HandHeart',
    pinColor: '#059669',
    subcategories: [
      'clothing',
      'babyItems',
      'moneyLoans',
      'medicalEquipment',
      'simchaItems',
      'tablesChairs',
      'furniture',
      'food',
      'sefarim',
      'tools',
      'other',
    ],
    defaultHoursMode: 'scheduled',
    usesHours: true,
  },
  {
    key: 'mikvah',
    slug: 'mikvaos',
    icon: 'Droplets',
    pinColor: '#0891b2',
    subcategories: ['mens', 'womens', 'keilim'],
    defaultHoursMode: 'scheduled',
    usesHours: true,
  },
  {
    key: 'beisHoraah',
    slug: 'batei-horaah',
    icon: 'Scale',
    pinColor: '#7c3aed',
    subcategories: ['beisHoraah', 'rav'],
    defaultHoursMode: 'scheduled',
    usesHours: true,
  },
  {
    key: 'organization',
    slug: 'organizations',
    icon: 'Users',
    pinColor: '#dc2626',
    subcategories: ['bikurCholimChesed', 'emergency', 'sefarimLibraries', 'tzedakah', 'other'],
    defaultHoursMode: 'callFirst',
    usesHours: true,
  },
  {
    key: 'service',
    slug: 'services',
    icon: 'Store',
    pinColor: '#d97706',
    subcategories: ['simchaHalls', 'shabbosApartments', 'eruv'],
    defaultHoursMode: 'callFirst',
    usesHours: true,
  },
  {
    key: 'shiur',
    slug: 'shiurim',
    icon: 'BookOpen',
    pinColor: '#be185d',
    // Subcategory = the topic; 'other' lets the owner type their own (topicOtherHe/En).
    subcategories: ['dafYomi', 'gemara', 'halacha', 'parsha', 'tanach', 'mussar', 'chassidus', 'emunah', 'other'],
    defaultHoursMode: 'scheduled',
    // Shiurim have their own sessions (details.sessions), not opening hours.
    usesHours: false,
  },
];

export const CATEGORY_KEYS: CategoryKey[] = CATEGORIES.map((c) => c.key);

export function getCategory(key: CategoryKey): CategoryConfig {
  const found = CATEGORIES.find((c) => c.key === key);
  if (!found) throw new Error(`Unknown category: ${key}`);
  return found;
}

export function getCategoryBySlug(slug: string): CategoryConfig | undefined {
  return CATEGORIES.find((c) => c.slug === slug);
}

/** Every subcategory key across all categories, without duplicates (e.g. 'other'). */
export const ALL_SUBCATEGORY_KEYS: string[] = Array.from(
  new Set(CATEGORIES.flatMap((c) => [...c.subcategories]))
);

/** Beis Horaah topic keys; labels from i18n `directory.topic.<key>`. */
export const BEIS_HORAAH_TOPICS = ['kashrus', 'shabbos', 'monetary', 'family', 'other'] as const;

/** Languages a Beis Horaah / Rav can answer in; labels from i18n `directory.language.<code>`. */
export const LANGUAGE_CODES = ['he', 'en', 'yi', 'fr', 'es', 'ru'] as const;

/** Who a shiur is for; labels from i18n `directory.audience.<key>`. */
export const SHIUR_AUDIENCES = ['men', 'women', 'children', 'everyone'] as const;

/** Default minutes after Mincha for "between Mincha and Maariv" (Yoel, 2026-10-22). */
export const BETWEEN_MINCHA_MAARIV_DEFAULT_MINUTES = 15;
